import type { CollectionResourceType } from "@/shared/domain/collections";
import {
  RobotoDomainException,
  RobotoNotFoundException,
  RobotoUnauthorizedException,
} from "@/shared/http/exceptions";

import {
  RESOURCE_TYPE_LABELS,
  describeId,
  findTypeMismatches,
} from "./collectionResourceIds";

/**
 * Shared reading of the raw LLM params for the two collection client tools.
 *
 * The confirmation modal and `execute` share the *verdict* on a proposal, not
 * just the parsing of it: each `coerce*` function returns either the payload
 * both sides act on or the single reason the write will be refused. Sharing
 * only the parsing is what let the preview render an approvable card for a
 * proposal `execute` then rejected.
 *
 * The two `*Params` types declare every field as `unknown` on purpose. Nothing
 * on them is readable as a string or an array without going through the
 * coercion, so a second hand-rolled reading does not typecheck.
 */

/**
 * The member types a collection can hold.
 *
 * Derived from {@link RESOURCE_TYPE_LABELS}, a `Record` over the union, so the
 * list cannot fall behind a new member type.
 */
export const RESOURCE_TYPES: readonly CollectionResourceType[] = Object.keys(
  RESOURCE_TYPE_LABELS,
) as CollectionResourceType[];

/**
 * Most IDs one proposal may carry.
 *
 * Both tools are pinned to REQUIRE_APPROVAL, so the confirmation card is the
 * only mitigation — and a list too long to read is not reviewed, it is waved
 * through.
 */
export const MAX_PROPOSED_IDS = 100;

/**
 * A reading of a proposal: the payload to act on, or why it will be refused.
 *
 * `details` carries structured context for the model's retry (e.g. the exact
 * IDs at fault); `reason` is shown to the user as well.
 */
export type ProposalVerdict<T> =
  | { ok: true; payload: T }
  | { ok: false; reason: string; details?: Record<string, unknown> };

/** A string param, or `undefined` when absent, non-string, or blank. */
export function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== ""
    ? value.trim()
    : undefined;
}

/**
 * The unique string entries of an array param; `[]` for anything else.
 *
 * Deduped because both the collection API's add (an upsert) and its remove
 * (a `DELETE ... WHERE`) treat a repeat as a no-op, so a duplicate is not a
 * mistake worth failing the whole request over — but it would otherwise
 * inflate the counts shown to the user and emit a duplicate React key.
 */
export function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? [
        ...new Set(
          value.filter((entry): entry is string => typeof entry === "string"),
        ),
      ]
    : [];
}

/** How many entries of an array param were not strings. */
function countNonStrings(value: unknown): number {
  return Array.isArray(value)
    ? value.filter((entry) => typeof entry !== "string").length
    : 0;
}

function asResourceType(value: unknown): CollectionResourceType | undefined {
  return RESOURCE_TYPES.find((candidate) => candidate === value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function countResourceRefObjects(value: unknown): number {
  return Array.isArray(value)
    ? value.filter((entry) => isRecord(entry) && "resource_id" in entry).length
    : 0;
}

function isPresentNonList(value: unknown): boolean {
  return value !== undefined && value !== null && !Array.isArray(value);
}

/**
 * Raw params of `client_create_collection`, as the model may emit them.
 *
 * Every field is `unknown` because the model is free to send anything;
 * `coerceCreateCollectionInput` is the only supported way to read one.
 */
export interface CreateCollectionParams {
  name?: unknown;
  resource_type?: unknown;
  description?: unknown;
  resources?: unknown;
  tags?: unknown;
}

/** Raw params of `client_add_to_collection`. See {@link CreateCollectionParams}. */
export interface AddToCollectionParams {
  collection_id?: unknown;
  add?: unknown;
  remove?: unknown;
}

/**
 * Whether a proposal is a JSON object at all.
 *
 * Deliberately the *only* thing these guards check. Everything else is reported
 * by the coercion with a message naming the actual problem, which a boolean
 * guard cannot do. Rejecting those here would replace an actionable error with
 * "Invalid input".
 */
export function isCreateCollectionParams(
  raw: unknown,
): raw is CreateCollectionParams {
  return isRecord(raw);
}

/** See {@link isCreateCollectionParams}. */
export function isAddToCollectionParams(
  raw: unknown,
): raw is AddToCollectionParams {
  return isRecord(raw);
}

/** A `client_create_collection` proposal that will be sent as-is. */
export interface CreateCollectionDraft {
  name: string;
  description?: string;
  resourceType: CollectionResourceType;
  tags: string[];
  resources: string[];
}

export function coerceCreateCollectionInput(
  params: CreateCollectionParams,
): ProposalVerdict<CreateCollectionDraft> {
  const name = asString(params.name);
  if (!name) {
    return {
      ok: false,
      reason: "name is required, and must be a non-blank string",
    };
  }

  // Never fall through to the API's default resource_type: it silently creates
  // a file collection, and every later add would then fail with a confusing
  // type-mismatch error far from the real cause.
  const resourceType = asResourceType(params.resource_type);
  if (!resourceType) {
    return {
      ok: false,
      reason:
        `resource_type must be one of ${RESOURCE_TYPES.join(", ")}; got ` +
        `${JSON.stringify(params.resource_type)}.`,
    };
  }

  // Refuse rather than silently drop. A bare "ds_1" where an array belongs
  // would otherwise create an *empty* collection and report success, leaving
  // the model to tell the user it was populated.
  if (isPresentNonList(params.resources)) {
    return {
      ok: false,
      reason:
        "'resources' must be an array of ID strings, even for a single ID; " +
        `got ${JSON.stringify(params.resources)}. Retry with ` +
        `resources: [${JSON.stringify(params.resources)}].`,
    };
  }

  const droppedResourceCount = countNonStrings(params.resources);
  if (droppedResourceCount > 0) {
    return {
      ok: false,
      reason:
        `'resources' must contain only ID strings; ${droppedResourceCount} ` +
        "entry(s) were not strings and cannot be used as members.",
    };
  }

  const resources = asStringArray(params.resources);
  if (resources.length > MAX_PROPOSED_IDS) {
    return {
      ok: false,
      reason:
        `'resources' carries ${resources.length} IDs, more than the ` +
        `${MAX_PROPOSED_IDS} a person can review in one approval. Create the ` +
        "collection with a smaller set, then add the rest in batches with " +
        "client_add_to_collection.",
    };
  }

  const mismatchedIds = findTypeMismatches(resources, resourceType);
  if (mismatchedIds.length > 0) {
    return {
      ok: false,
      reason:
        `A ${resourceType} collection can only hold ${resourceType}s, but ` +
        `${mismatchedIds.map(describeId).join("; ")}.`,
      details: { rejected_ids: mismatchedIds },
    };
  }

  return {
    ok: true,
    payload: {
      name,
      description: asString(params.description),
      resourceType,
      tags: asStringArray(params.tags),
      resources,
    },
  };
}

/** A `client_add_to_collection` proposal that will be sent as-is. */
export interface AddToCollectionDraft {
  collectionId: string;
  add: string[];
  remove: string[];
}

export function coerceAddToCollectionInput(
  params: AddToCollectionParams,
): ProposalVerdict<AddToCollectionDraft> {
  const collectionId = asString(params.collection_id);
  if (!collectionId) {
    return {
      ok: false,
      reason: "collection_id is required, and must be a non-blank string",
    };
  }

  // Name the real problem. The likeliest malformed shape is the API's own wire
  // form -- exactly what the model sees coming back from the collection reads
  // -- and reporting that as "provide at least one ID" sends it looking in the
  // wrong place.
  const resourceRefObjectCount =
    countResourceRefObjects(params.add) +
    countResourceRefObjects(params.remove);
  if (resourceRefObjectCount > 0) {
    return {
      ok: false,
      reason:
        "'add' and 'remove' take bare ID strings, not {resource_id, " +
        "resource_type} objects. Retry with just the resource_id values, " +
        'e.g. add: ["ds_1", "ds_2"].',
    };
  }

  if (isPresentNonList(params.add) || isPresentNonList(params.remove)) {
    return {
      ok: false,
      reason:
        "'add' and 'remove' must be arrays of ID strings, even for a single " +
        `ID; got add=${JSON.stringify(params.add)}, ` +
        `remove=${JSON.stringify(params.remove)}.`,
    };
  }

  // Refuse rather than silently drop: entries that are not ID strings would
  // otherwise vanish, and the model would be told the edit succeeded in full.
  const droppedIdCount =
    countNonStrings(params.add) + countNonStrings(params.remove);
  if (droppedIdCount > 0) {
    return {
      ok: false,
      reason:
        `'add' and 'remove' must contain only ID strings; ${droppedIdCount} ` +
        "entry(s) could not be used.",
    };
  }

  const add = asStringArray(params.add);
  const remove = asStringArray(params.remove);
  if (add.length === 0 && remove.length === 0) {
    return {
      ok: false,
      reason: "Provide at least one ID in 'add' or 'remove'",
    };
  }

  const proposedIdCount = add.length + remove.length;
  if (proposedIdCount > MAX_PROPOSED_IDS) {
    return {
      ok: false,
      reason:
        `'add' and 'remove' carry ${proposedIdCount} IDs between them, more ` +
        `than the ${MAX_PROPOSED_IDS} a person can review in one approval. ` +
        "Split the edit into smaller batches.",
    };
  }

  return { ok: true, payload: { collectionId, add, remove } };
}

/**
 * What went wrong with a collection request, in terms safe to store.
 *
 * The service embeds the caller's user ID in its authorization messages, and a
 * tool result lives in the thread transcript permanently, so the exception
 * class stands in for the server's prose. The class is what the model needs to
 * decide what to do next; the prose was never more than flavour.
 */
export function describeCollectionFailure(error: unknown): string {
  if (error instanceof RobotoUnauthorizedException) {
    return "the caller is not authorized to make this change";
  }
  if (error instanceof RobotoNotFoundException) {
    return "it does not exist, or is not visible to this org";
  }
  if (error instanceof RobotoDomainException) {
    return error.isClientError()
      ? `the service rejected the request (${error.name})`
      : `the service could not complete the request (${error.name})`;
  }
  return "the request did not complete";
}
